import { useWalletBalanceInfo } from '@/hooks/useWalletBalancesInfo';
import { formatCurrencyToLocale, toFixedDecimals } from '@/utils';
import {
  Box,
  Button,
  Flex,
  FormatNumber,
  Heading,
  HStack,
  Image,
  ProgressCircle,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useProfileWalletAddress } from './useProfileWalletAddress';
import plumeSvgPath from '/assets/tokens/plume.svg';

export const DEFAULT_VISIBLE_ASSETS = 4;

type Props = {
  setShowAllAssets: (showAllAssets: boolean) => void;
  showAllAssets: boolean;
};

export const AssetsTable = ({ showAllAssets, setShowAllAssets }: Props) => {
  const { profileAddress } = useProfileWalletAddress();
  const { walletTokenBalanceInfoArr, totalUSDValueOfWallet, isLoading } = useWalletBalanceInfo(profileAddress);
  const { address: userAddress } = useAccount();

  // sort tokenBalanceInfo so that anthing with plume in the name is sorted first
  const sortedTokenBalanceInfo =
    walletTokenBalanceInfoArr?.sort((a, b) => {
      return b.holdings.valueUSD - a.holdings.valueUSD;

      // // Priority 1: Tokens with "plume" in the name
      // if (a.token.name.toLowerCase().includes('plume') && !b.token.name.toLowerCase().includes('plume')) return -1;
      // if (b.token.name.toLowerCase().includes('plume') && !a.token.name.toLowerCase().includes('plume')) return 1;

      // // Priority 2: Tokens starting with 'n' in their symbol
      // if (a.token.symbol.startsWith('n') && !b.token.symbol.startsWith('n')) return -1;
      // if (b.token.symbol.startsWith('n') && !a.token.symbol.startsWith('n')) return 1;
      // if (a.token.symbol.startsWith('op-n') && !b.token.symbol.startsWith('n')) return -1;
      // if (b.token.symbol.startsWith('op-n') && !a.token.symbol.startsWith('n')) return 1;
      // if (a.token.symbol.startsWith('in') && !b.token.symbol.startsWith('n')) return -1;
      // if (b.token.symbol.startsWith('in') && !a.token.symbol.startsWith('n')) return 1;

      // If both are in the same category, keep standard alphabetical order
      // return a.token.name.localeCompare(b.token.name);
    }) ?? [];

  const handleRowClick = (address: string) => {
    window.open(`https://explorer.plume.org/address/${address}`, '_blank');
  };

  const numHiddenAssets = sortedTokenBalanceInfo.length - DEFAULT_VISIBLE_ASSETS;

  const protocolUrls: Record<string, string> = {
    Nest: 'https://app.nest.credit/',
    Solera: 'https://app.solera.market/',
    Mystic: 'https://app.mysticfinance.xyz/',
    Rooster: 'https://www.rooster-protocol.xyz/?chain=98866',
  };
  const protocolList = ['Nest', 'Solera', 'Mystic', 'Rooster'];

  return (
    <>
      <VStack alignItems="flex-start" gap={2}>
        <Heading fontWeight={'700'} fontSize={'2xl'}>
          Assets
        </Heading>
        <HStack w="full" justifyContent="space-between">
          {isLoading ? (
            <Skeleton
              height="48px"
              width="180px"
              mb={8}
              fontFamily="Lufga, sans-serif"
              fontWeight={'500'}
              fontSize={'5xl'}
            />
          ) : (
            <Heading fontFamily="Lufga, sans-serif" fontWeight={'600'} fontSize={'4xl'}>
              {formatCurrencyToLocale(totalUSDValueOfWallet)}
            </Heading>
          )}
        </HStack>
      </VStack>

      {isLoading ? (
        <AssetTableSkeleton />
      ) : (
        <>
          {walletTokenBalanceInfoArr?.length || 0 ? (
            <Box overflowX="auto" w="100%" maxW="100%" position="relative" minH="400px" mb={4}>
              <Table.Root
                borderCollapse="separate"
                borderSpacing={'0 10px'}
                w="100%"
                position={['absolute', 'absolute', 'relative']}
                minH="min-content"
              >
                <Table.Header>
                  <Table.Row bg="transparent">
                    <Table.ColumnHeader
                      color="text.secondary"
                      fontSize={'sm'}
                      alignContent={'end'}
                      paddingX={3}
                      borderBottom={'none'}
                      fontWeight={'normal'}
                      whiteSpace="nowrap"
                    >
                      Position
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      color="text.secondary"
                      fontSize={'sm'}
                      alignContent={'end'}
                      paddingX={3}
                      borderBottom={'none'}
                      fontWeight={'normal'}
                      whiteSpace="nowrap"
                    >
                      Protocol
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      color="text.secondary"
                      fontSize={'sm'}
                      alignContent={'end'}
                      paddingX={3}
                      borderBottom={'none'}
                      fontWeight={'normal'}
                      whiteSpace="nowrap"
                    >
                      Balance
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      color="text.secondary"
                      fontSize={'sm'}
                      alignContent={'end'}
                      paddingX={3}
                      borderBottom={'none'}
                      fontWeight={'normal'}
                      whiteSpace="nowrap"
                    >
                      Balance %
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      color="text.secondary"
                      fontSize={'sm'}
                      alignContent={'end'}
                      paddingX={3}
                      borderBottom={'none'}
                      fontWeight={'normal'}
                      whiteSpace="nowrap"
                    >
                      Price (24h)
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      color="text.secondary"
                      fontSize={'sm'}
                      alignContent={'end'}
                      paddingX={3}
                      borderBottom={'none'}
                      fontWeight={'normal'}
                      whiteSpace="nowrap"
                    />
                  </Table.Row>
                </Table.Header>
                <Table.Body fontFamily="Lufga, sans-serif" fontSize={'md'}>
                  {sortedTokenBalanceInfo
                    .slice(0, showAllAssets ? sortedTokenBalanceInfo.length : DEFAULT_VISIBLE_ASSETS)
                    .map(
                      (
                        {
                          token: {
                            address,
                            imageLargeUrl,
                            name,
                            symbol,
                            priceUSD,
                            change24Float,
                            change24,
                            protocol,
                            protocolIcon,
                            tokenType,
                            decimals,
                          },
                          holdings: { tokenBalance, valueUSD },
                        },
                        idx,
                      ) => {
                        const percent = totalUSDValueOfWallet > 0 ? (valueUSD / totalUSDValueOfWallet) * 100 : 0;

                        return (
                          <Table.Row
                            key={`${address}-${symbol}-${idx}`}
                            onClick={() => handleRowClick(address)}
                            _hover={{ cursor: 'pointer' }}
                            bg="white"
                          >
                            <Table.Cell
                              minW="180px"
                              maxW={240}
                              borderTopLeftRadius="xl"
                              borderBottomLeftRadius="xl"
                              py={4}
                              pl={8}
                              border="1px solid #f0f0f0"
                              borderRight="none"
                              whiteSpace="nowrap"
                            >
                              <HStack gap={2}>
                                <Image
                                  // hardcode this conditional b/c native asset price lookup uses wPLUME wrapper and shows that icon by default
                                  src={
                                    (symbol === 'PLUME' ? plumeSvgPath : imageLargeUrl) ||
                                    '/assets/tokens/default-token.svg'
                                  }
                                  boxSize={8}
                                  borderRadius="full"
                                />
                                <Box>
                                  <Text>{name.replace('Nest RWA Vault', 'Nest NALPHA Vault')}</Text>
                                  <Text color="colorPalette.500" fontSize={'sm'}>
                                    {symbol.replace('nRWA', 'nALPHA')}
                                  </Text>
                                </Box>
                              </HStack>
                            </Table.Cell>
                            <Table.Cell border="1px solid #f0f0f0" borderRight="none" borderLeft="none">
                              <HStack
                                gap={2}
                                py={2}
                                px={4}
                                ml={-4}
                                rounded="full"
                                w="fit-content"
                                onClick={(e) => {
                                  if (protocolList.includes(protocol)) {
                                    e.stopPropagation(); // Prevent triggering the row click
                                    const url = protocolUrls[protocol];
                                    if (url) {
                                      window.open(url, '_blank');
                                    }
                                  }
                                }}
                                _hover={{
                                  bg: protocolList.includes(protocol) ? 'bg.subtle' : 'none',
                                }}
                              >
                                {protocolIcon && (
                                  <Image
                                    src={protocolIcon}
                                    boxSize={6}
                                    borderRadius="full"
                                    filter={`grayscale(${protocolIcon === '/assets/apps/plume_staking.avif' ? '100%' : '0%'})`}
                                  />
                                )}
                                <Text>{protocol}</Text>
                              </HStack>
                            </Table.Cell>
                            <Table.Cell
                              border="1px solid #f0f0f0"
                              borderRight="none"
                              borderLeft="none"
                              whiteSpace="nowrap"
                            >
                              {priceUSD !== null ? formatCurrencyToLocale(valueUSD) : ' '}
                              <Text color="colorPalette.500" fontSize={'sm'}>
                                <FormatNumber value={tokenBalance} maximumSignificantDigits={4} />{' '}
                                {symbol.replace('nRWA', 'nALPHA')}
                              </Text>
                            </Table.Cell>
                            <Table.Cell border="1px solid #f0f0f0" borderRight="none" borderLeft="none">
                              {priceUSD !== null ? (
                                <HStack>
                                  <ProgressCircle.Root value={Math.max(Math.min(percent, 100), 0)} size="xs">
                                    <ProgressCircle.Circle css={{ '--thickness': '8px' }}>
                                      <ProgressCircle.Track stroke="#D4FFF0" />
                                      <ProgressCircle.Range stroke="#00D594" />
                                    </ProgressCircle.Circle>
                                  </ProgressCircle.Root>
                                  <Text color="colorPalette.500">{percent.toFixed(1)}%</Text>
                                </HStack>
                              ) : (
                                <Text> </Text>
                              )}
                            </Table.Cell>
                            <Table.Cell border="1px solid #f0f0f0" borderRight="none" borderLeft="none">
                              <Text>{priceUSD !== null && formatCurrencyToLocale(Number(priceUSD))}</Text>
                              {change24 && parseFloat(change24) > 0.01 && change24Float && change24Float >= 0.01 && (
                                <Text color={change24Float > 0 ? 'green.400' : 'red.400'} fontSize={'sm'}>
                                  {toFixedDecimals(change24 || '0')}%
                                </Text>
                              )}
                            </Table.Cell>
                            <Table.Cell
                              borderTopRightRadius="xl"
                              borderBottomRightRadius="xl"
                              border="1px solid #f0f0f0"
                              borderLeft="none"
                              w={10}
                              pr={6}
                            >
                              <Flex justifyContent="end">
                                {tokenType === 'erc20' && !!userAddress && (
                                  <AddTokenToWalletButton
                                    tokenSymbol={symbol}
                                    label="Add"
                                    tokenAddress={address}
                                    tokenDecimals={Number(decimals)}
                                    tokenImageUrl={imageLargeUrl}
                                    iconStyleProps={{
                                      boxSize: 4,
                                    }}
                                  />
                                )}
                              </Flex>
                            </Table.Cell>
                          </Table.Row>
                        );
                      },
                    )}
                </Table.Body>
              </Table.Root>
            </Box>
          ) : null}
          {numHiddenAssets > 0 && (
            <Button
              onClick={() => setShowAllAssets(!showAllAssets)}
              variant="outline"
              rounded="xl"
              borderColor="bg.muted"
              h={74}
              w="full"
              bg="bg"
              _hover={{
                bg: 'bg.muted',
              }}
            >
              {showAllAssets ? 'Minimise' : 'View All'}
              {showAllAssets ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M13.75 10C13.75 10.1658 13.6842 10.3247 13.5669 10.4419C13.4497 10.5592 13.2908 10.625 13.125 10.625H6.875C6.70924 10.625 6.55027 10.5592 6.43306 10.4419C6.31585 10.3247 6.25 10.1658 6.25 10C6.25 9.83424 6.31585 9.67527 6.43306 9.55806C6.55027 9.44085 6.70924 9.375 6.875 9.375H13.125C13.2908 9.375 13.4497 9.44085 13.5669 9.55806C13.6842 9.67527 13.75 9.83424 13.75 10ZM18.125 10C18.125 11.607 17.6485 13.1779 16.7557 14.514C15.8629 15.8502 14.594 16.8916 13.1093 17.5065C11.6247 18.1215 9.99099 18.2824 8.4149 17.9689C6.8388 17.6554 5.39106 16.8815 4.25476 15.7452C3.11846 14.6089 2.34463 13.1612 2.03112 11.5851C1.71762 10.009 1.87852 8.37535 2.49348 6.8907C3.10844 5.40605 4.14985 4.1371 5.486 3.24431C6.82214 2.35152 8.39303 1.875 10 1.875C12.1542 1.87727 14.2195 2.73403 15.7427 4.25727C17.266 5.78051 18.1227 7.84581 18.125 10ZM16.875 10C16.875 8.64025 16.4718 7.31104 15.7164 6.18045C14.9609 5.04987 13.8872 4.16868 12.631 3.64833C11.3747 3.12798 9.99238 2.99183 8.65876 3.2571C7.32514 3.52237 6.10013 4.17716 5.13864 5.13864C4.17716 6.10013 3.52238 7.32513 3.2571 8.65875C2.99183 9.99237 3.12798 11.3747 3.64833 12.6309C4.16868 13.8872 5.04987 14.9609 6.18046 15.7164C7.31105 16.4718 8.64026 16.875 10 16.875C11.8227 16.8729 13.5702 16.1479 14.8591 14.8591C16.1479 13.5702 16.8729 11.8227 16.875 10Z"
                    fill="#747474"
                  />
                </svg>
              ) : (
                <Box px={2} py={0.5} bg="bg.subtle" rounded="full">
                  +{numHiddenAssets}
                </Box>
              )}
            </Button>
          )}
        </>
      )}
    </>
  );
};

import { AddTokenToWalletButton } from '@/components/add-token-to-wallet-button';
import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import { useAccount } from 'wagmi';

export const AssetTableSkeleton = () => {
  return (
    <>
      <Box overflow="auto">
        <Table.Root borderCollapse="separate" borderSpacing={'0 10px'}>
          <Table.Header>
            <Table.Row bg="transparent">
              <Table.ColumnHeader
                color="text.secondary"
                fontSize={'sm'}
                alignContent={'end'}
                padding={0}
                borderBottom={'none'}
                fontWeight={'normal'}
              >
                <Skeleton height="16px" width="40px" />
              </Table.ColumnHeader>
              <Table.ColumnHeader
                color="text.secondary"
                fontSize={'sm'}
                alignContent={'end'}
                padding={0}
                borderBottom={'none'}
                fontWeight={'normal'}
              >
                <Skeleton height="16px" width="60px" />
              </Table.ColumnHeader>
              <Table.ColumnHeader
                color="text.secondary"
                fontSize={'sm'}
                alignContent={'end'}
                padding={0}
                borderBottom={'none'}
                fontWeight={'normal'}
              >
                <Skeleton height="16px" width="60px" />
              </Table.ColumnHeader>
              <Table.ColumnHeader
                color="text.secondary"
                fontSize={'sm'}
                alignContent={'end'}
                padding={0}
                borderBottom={'none'}
                fontWeight={'normal'}
              >
                <Skeleton height="16px" width="70px" />
              </Table.ColumnHeader>
              <Table.ColumnHeader
                color="text.secondary"
                fontSize={'sm'}
                alignContent={'end'}
                padding={0}
                borderBottom={'none'}
                fontWeight={'normal'}
              >
                <Skeleton height="16px" width="80px" />
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body fontFamily="Lufga, sans-serif" fontSize={'sm'}>
            {[...Array(3)].map((_, index) => (
              <Table.Row key={index} bg="white">
                <Table.Cell minW={50} borderTopLeftRadius="xl" borderBottomLeftRadius="xl" mb={6}>
                  <HStack gap={2}>
                    <SkeletonCircle size="8" />
                    <Box>
                      <Skeleton height="20px" width="100px" mb={1} />
                      <Skeleton height="14px" width="60px" />
                    </Box>
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={2}>
                    <SkeletonCircle size="6" />
                    <Skeleton height="20px" width="80px" />
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <Skeleton height="20px" width="80px" mb={1} />
                  <Skeleton height="14px" width="60px" />
                </Table.Cell>
                <Table.Cell>
                  <HStack>
                    <SkeletonCircle size="5" />
                    <Skeleton height="20px" width="40px" />
                  </HStack>
                </Table.Cell>
                <Table.Cell borderTopRightRadius="lg" borderBottomRightRadius="lg">
                  <Skeleton height="20px" width="60px" mb={1} />
                  <Skeleton height="14px" width="40px" />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </>
  );
};
